import React, { useEffect, useState } from 'react'
import Actions, { useDispatch } from 'Flux/index.ts'
import { orderSlice } from 'Flux/Slice/Order/OrderSlice.ts'
import { useNavigate } from 'react-router-dom'
import { useGetAuthenticatedUser, useGetOrderSelector, useGetSamplePoolSelector } from 'Flux/Selector/index.ts'
import type { TaxaOption } from './TaxaOptions.tsx'
import { taxaOptions } from './TaxaOptions.tsx'
import { FormControl, Label, Paragraph } from '../../../Common/index.ts'
import { addMessage } from '../../../../Flux/Slice/Application/ApplicationSlice.ts'
import { infoMessage } from '../../../../Model/Message/IMessage.ts'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.tsx'
import type { SampleConfigurationTypeKey } from '../../../../Generated/SampleConfigurationType.js'
import { SampleConfigurationTypes } from '../../../../Generated/SampleConfigurationType.js'
import type { ProcessingTypeKey } from '../../../../Generated/ProcessingType.ts'
import { ProcessingTypes } from '../../../../Generated/ProcessingType.ts'
import type { RedlistModeKey } from '../../../../Generated/RedlistMode.js'
import { RedlistModes } from '../../../../Generated/RedlistMode.js'
import { useModal } from '../../../Modal/UseModal.js'
import type { TargetRegionKey } from '../../../../Generated/TargetRegion.ts'
import { TargetRegions } from '../../../../Generated/TargetRegion.ts'
import { trackOrderCreationOption } from '../../../../Services/Telemetry/index.ts'
import {
  getAllowedTargetTaxaForProcessingType,
  getProcessingTypeAccentBg,
  getProcessingTypeAccentBorder,
  getProcessingTypeLabel,
  getProcessingTypeTaxaValidationMessage,
  isProcessingTypeTaxaCombinationValid
} from '../../../../Util/processingType.ts'
import { describeSampleConfiguration, getSampleConfigurationLabel } from '../../../../Util/sequencingPlatform.ts'
import { SequencingPlatformSettingRow } from '../../../Component/SequencingPlatformSettingRow.tsx'
import { OrderWizardCreatePageEntry } from '../../OrderWizard/module.tsx'

type Mode = 'customer' | 'lab'

interface CreateOrderCommonPageProps {
  mode: Mode
}

export const CreateOrderCommonPage = ({ mode }: CreateOrderCommonPageProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const createdOrder = useGetOrderSelector()

  const user = useGetAuthenticatedUser()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedTaxa, setSelectedTaxa] = useState<TaxaOption | null>(mode === 'lab' ? taxaOptions[0] : null)
  const [selectedRegion, setSelectedRegion] = useState<TargetRegionKey | null>(mode === 'lab' ? 'WORLDWIDE' : null)
  const [singletonAllowed, setSingletonAllowed] = useState<boolean | null>(mode === 'lab' ? false : null)
  const pools = useGetSamplePoolSelector()
  const [isLoading, setLoading] = useState(false)
  const [createInitialDataset, setCreateInitialDataset] = useState(true)
  const [sampleConfiguration, setSampleConfiguration] = useState<SampleConfigurationTypeKey | undefined>(undefined)
  const [processingTypeOverride, setProcessingTypeOverride] = useState<ProcessingTypeKey | null>(null)
  const processingType: ProcessingTypeKey = processingTypeOverride ?? user?.org.defaultProcessingType ?? 'METABARCODING'
  const [redlistMode, setRedlistMode] = useState<RedlistModeKey | undefined>(undefined)

  const [isResetted, setIsResetted] = useState<boolean>(false)

  const [showMore, setShowMore] = useState<boolean>(false)

  const { openModal } = useModal()

  useEffect(() => {
    if (!user) {
      return
    }

    if (mode == 'lab') {
      setSampleConfiguration(user.org.defaultSampleConfigurationType)
      console.info('user is', user)
      console.info('setting redlist mode to ', user.org.defaultRedlistMode)
      setRedlistMode(user.org.defaultRedlistMode)
    }
  }, [user, mode])

  useEffect(() => {
    if (mode === 'lab') {
      if (!selectedTaxa) setSelectedTaxa(taxaOptions[0])
      if (singletonAllowed === null) setSingletonAllowed(false)
    }
  }, [mode])

  useEffect(() => {
    const allowedTaxa = getAllowedTargetTaxaForProcessingType(processingType)
    if (allowedTaxa === null || allowedTaxa.includes(selectedTaxa?.value ?? 'OTHER')) {
      return
    }
    const fallbackTaxa = taxaOptions.find((option) => allowedTaxa.includes(option.value))
    setSelectedTaxa(fallbackTaxa ?? null)
  }, [processingType, selectedTaxa])

  useEffect(() => {
    dispatch(Actions[orderSlice.name].resetOrder())
    setIsResetted(true)
  }, [])

  useEffect(() => {
    if (isResetted) {
      if (createdOrder && createdOrder !== 'LOADING') {
        if (pools.length > 0 || mode === 'customer' || !createInitialDataset) {
          navigate(`/order/${createdOrder.id}`)
        } else {
          dispatch(Actions[orderSlice.name].createSamplepoolRequest({ orderId: createdOrder.id }))
          setLoading(true)
        }
      }
    }
  }, [createdOrder, pools])

  const handleSubmit = () => {
    if (!name.trim()) {
      dispatch(addMessage(infoMessage('Please provide a non-empty order name')))
      return
    }

    if (mode === 'customer') {
      if (!isProcessingTypeTaxaCombinationValid(processingType, selectedTaxa?.value ?? null)) {
        dispatch(addMessage(infoMessage(getProcessingTypeTaxaValidationMessage(processingType))))
        return
      }
      dispatch(
        Actions.order.createOrderDraftRequest({
          name: name.trim(),
          description: description.trim() || undefined,
          targetTaxonomy: selectedTaxa?.value,
          targetRegion: selectedTaxa?.value === 'CO1_ANIMAL' ? (selectedRegion ?? undefined) : undefined,
          allowSingletons: singletonAllowed ?? undefined,
          sampleConfiguration: sampleConfiguration,
          processingType,
          redlistMode: redlistMode
        })
      )
    } else {
      if (!selectedTaxa || singletonAllowed === null) {
        dispatch(addMessage(infoMessage('Please complete all required fields.')))
        return
      }

      if (!isProcessingTypeTaxaCombinationValid(processingType, selectedTaxa.value)) {
        dispatch(addMessage(infoMessage(getProcessingTypeTaxaValidationMessage(processingType))))
        return
      }

      dispatch(
        Actions.order.createOrderRequest({
          name: name.trim(),
          description: description.trim() || undefined,
          targetTaxonomy: selectedTaxa.value,
          targetRegion: selectedTaxa.value === 'CO1_ANIMAL' ? (selectedRegion ?? undefined) : undefined,
          allowSingletons: singletonAllowed ?? undefined,
          sampleConfiguration: sampleConfiguration ?? 'SINGLE_SAMPLE_PAIRS',
          processingType,
          redlistMode: redlistMode ?? user?.org.defaultRedlistMode ?? 'NONE'
        })
      )
    }
  }

  return (
    <div>
      {mode === 'lab' && <OrderWizardCreatePageEntry />}
      <div
        className={`relative overflow-hidden w-full bg-white shadow-lg border border-gray-300 rounded-2xl p-3 sm:p-6 md:p-12 max-w-2xl mx-auto transition-colors duration-300 ${getProcessingTypeAccentBorder(processingType)}`}
      >
        <span
          className={`absolute top-0 left-0 text-xs font-semibold text-white px-2.5 py-1 rounded-br-md ${getProcessingTypeAccentBg(processingType)}`}
        >
          {getProcessingTypeLabel(processingType)}
        </span>
        <h1 className="text-2xl text-primary font-bold text-center">
          {mode === 'lab' ? (
            <TooltipTrigger tooltip={'Create an order with analyzed fastq files for processing by spcfy'} withIcon>
              <span>Create an Order with Fastq Files</span>
            </TooltipTrigger>
          ) : (
            <TooltipTrigger
              tooltip={
                'Prepare an order without fully analyzed samples by adding sample metadata and then invite a lab for further processing'
              }
              withIcon
            >
              <span>Prepare an Order Draft</span>
            </TooltipTrigger>
          )}
        </h1>
        <hr />
        <div className="text-center">
          <Paragraph>Please assign an order name in order to continue uploading your files.</Paragraph>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim().length === 0 || isLoading) return
            handleSubmit()
          }}
        >
          <FormControl>
            <Label>Name your order (mandatory)</Label>
            <input
              data-testid="create-order-name"
              type="text"
              className="w-full border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </FormControl>
          <FormControl>
            <Label>Description (optional)</Label>
            <textarea
              data-testid="create-order-description"
              className="w-full border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-2 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 min-h-24"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <Label valid={true}>Target taxon: {mode === 'lab' ? '(mandatory)' : '(optional)'}</Label>
            <select
              className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
              value={selectedTaxa?.value || ''}
              data-testid="create-order-taxa-select"
              onChange={(e) => {
                const found = taxaOptions.find((t) => t.value === e.target.value)
                trackOrderCreationOption('taxon', e.target.value, mode)
                setSelectedTaxa(found || null)
              }}
            >
              {mode === 'customer' && <option value="">Unset (leave empty)</option>}
              {taxaOptions.map((taxon) => (
                <option
                  key={taxon.value}
                  value={taxon.value}
                  disabled={taxon.disabled || !isProcessingTypeTaxaCombinationValid(processingType, taxon.value)}
                >
                  {taxon.label}
                </option>
              ))}
            </select>
          </FormControl>
          {!isProcessingTypeTaxaCombinationValid(processingType, selectedTaxa?.value ?? null) && (
            <p className="mt-2 text-sm text-error">{getProcessingTypeTaxaValidationMessage(processingType)}</p>
          )}
          {selectedTaxa?.value === 'CO1_ANIMAL' && (
            <FormControl>
              <Label valid={true}>Target region: {mode === 'lab' ? '(mandatory)' : '(optional)'}</Label>
              <select
                className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
                value={selectedRegion || ''}
                onChange={(e) => {
                  const found = Object.values(TargetRegions).find((t) => t.key === e.target.value)
                  trackOrderCreationOption('region', e.target.value, mode)
                  setSelectedRegion(found?.key || null)
                }}
              >
                {mode === 'customer' && <option value="">Unset (leave empty)</option>}
                {Object.values(TargetRegions).map((region) => (
                  <option key={region.key ?? 'null'} value={region.key ?? ''}>
                    {region.label}
                  </option>
                ))}
              </select>
            </FormControl>
          )}
          <div className="mt-4 flex flex-col sm:flex-row justify-between items-stretch gap-2">
            <TooltipTrigger classNames={'inline-block sm:w-1/2'} tooltip={'More configuration options'}>
              <button
                data-testid="expand-options"
                type="button"
                onClick={() => setShowMore((p) => !p)}
                className={'btn md:w-auto md:btn-xs sm:max-md:btn-lg w-full sm:w-full'}
              >
                {showMore ? 'Less' : 'More Options'}
              </button>
            </TooltipTrigger>
            <TooltipTrigger
              classNames={'inline-block md:w-auto sm:w-1/2'}
              tooltip={'You need to provide at least an identifying name for your order'}
              disabled={name.length !== 0}
            >
              <button
                type={'submit'}
                data-testid="create-order-btn"
                className="btn btn-primary w-full md:w-auto sm:w-full sm:max-md:btn-lg md:btn"
                disabled={name.length === 0 || isLoading}
              >
                Next
              </button>
            </TooltipTrigger>
          </div>
        </form>
        {showMore && (
          <div className={'mt-4'}>
            <FormControl>
              <Label valid={true}>Processing type</Label>
              <select
                className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
                value={processingType}
                onChange={(e) => {
                  trackOrderCreationOption('processing_type', e.target.value, mode, { is_override: true })
                  setProcessingTypeOverride(e.target.value as ProcessingTypeKey)
                }}
              >
                {Object.entries(ProcessingTypes).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </FormControl>
            <SequencingPlatformSettingRow />
            <FormControl>
              <TooltipTrigger
                tooltip={
                  'By default, spcfy removes singleton matches (i.e. species with only one read) from your results. If you are looking for invasive species, keeping singleton matches might be more desirable.'
                }
                withIcon
              >
                <Label inline valid={true}>
                  Should Singleton Matches be present in the results?
                </Label>
              </TooltipTrigger>
              <select
                className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
                value={singletonAllowed === null ? '' : singletonAllowed ? 'true' : 'false'}
                onChange={(e) => {
                  const val = e.target.value
                  trackOrderCreationOption('singletons', val === '' ? 'unset' : val, mode)
                  setSingletonAllowed(val === '' ? null : val === 'true')
                }}
              >
                {mode === 'customer' && <option value="">Unset (leave empty)</option>}
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </FormControl>
            <FormControl>
              <TooltipTrigger tooltip="Choose how your samples are formatted. This will guide how they are processed.">
                <Label inline valid={true}>
                  Sample Format
                </Label>
              </TooltipTrigger>
              <div className={'flex gap-4 items-end space-between'}>
                <select
                  data-testid={'sample-configuration'}
                  className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
                  value={sampleConfiguration}
                  onChange={(e) => {
                    trackOrderCreationOption('sample_configuration', e.target.value, mode)
                    setSampleConfiguration(e.target.value as SampleConfigurationTypeKey)
                  }}
                >
                  {mode === 'customer' && <option value="">Unset (leave empty)</option>}
                  {/* No sequencing platform is known yet at order creation (no files uploaded), so labels stay platform-generic. */}
                  {Object.keys(SampleConfigurationTypes).map((key) => (
                    <option key={key} value={key} title={describeSampleConfiguration(key as SampleConfigurationTypeKey, null)}>
                      {getSampleConfigurationLabel(key as SampleConfigurationTypeKey, null)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={'btn btn-outline btn-xs'}
                  onClick={() =>
                    openModal(
                      'sampleConfigurationType',
                      {
                        initial: sampleConfiguration ? user?.org?.defaultSampleConfigurationType : undefined
                      },
                      (result) => setSampleConfiguration(result)
                    )
                  }
                >
                  Help me choose
                </button>
              </div>
              {sampleConfiguration && (
                <p className="mt-1 text-xs text-gray-600" data-testid="sample-configuration-description">
                  {describeSampleConfiguration(sampleConfiguration, null)}
                </p>
              )}
            </FormControl>

            {selectedTaxa?.value === 'CO1_ANIMAL' && (
              <FormControl>
                <TooltipTrigger tooltip="Choose the redlist mode for this order.">
                  <Label inline valid={true}>
                    Redlist Mode
                  </Label>
                </TooltipTrigger>
                <select
                  className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
                  value={redlistMode}
                  onChange={(e) => {
                    trackOrderCreationOption('redlist_mode', e.target.value, mode)
                    setRedlistMode(e.target.value as RedlistModeKey)
                  }}
                >
                  {mode === 'customer' && <option value="">Unset (leave empty)</option>}
                  {Object.entries(RedlistModes).map(([key, { label }]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </FormControl>
            )}

            <FormControl>
              {mode === 'lab' && (
                <TooltipTrigger
                  tooltip={
                    'Fastq Files are arranged in pools to account for negative controls. ' +
                    'Since at least one pool is required, it will usually be pre-created. If for whatever reason you' +
                    ' do not wish the pre-create the first pool, uncheck this box'
                  }
                >
                  <label className="label">
                    <input
                      type="checkbox"
                      className="checkbox mr-2"
                      checked={createInitialDataset}
                      onChange={(e) => setCreateInitialDataset(e.target.checked)}
                    />
                    Create Initial Sample Pool
                  </label>
                </TooltipTrigger>
              )}
            </FormControl>
          </div>
        )}
      </div>
    </div>
  )
}
